import React, { useState, useEffect, useCallback } from 'react';
import Card from '../base/Card';
import Button from '../base/Button';
import './MicroResets.css';

const RESETS = [
  {
    id: 'neck-stretch',
    category: 'stretch',
    name: 'Neck & Shoulder Release',
    duration: 90, // seconds
    icon: '🙆',
    steps: [
      { time: 0, instruction: 'Sit tall with your feet flat on the floor', duration: 15 },
      { time: 15, instruction: 'Slowly tilt your head to the right, hold for 15 seconds', duration: 15 },
      { time: 30, instruction: 'Return to center, then tilt to the left, hold for 15 seconds', duration: 15 },
      { time: 45, instruction: 'Roll your shoulders back 5 times', duration: 15 },
      { time: 60, instruction: 'Gently turn your head to look over each shoulder', duration: 15 },
      { time: 75, instruction: 'Take a deep breath and relax', duration: 15 }
    ]
  },
  {
    id: 'breathing',
    category: 'breathing',
    name: '4-7-8 Breathing',
    duration: 120, // seconds (2 minutes)
    icon: '🫁',
    steps: [
      { time: 0, instruction: 'Sit comfortably with your back straight', duration: 10 },
      { time: 10, instruction: 'Exhale completely through your mouth', duration: 2 },
      { time: 12, instruction: 'Close your mouth and inhale through your nose for 4 counts', duration: 4 },
      { time: 16, instruction: 'Hold your breath for 7 counts', duration: 7 },
      { time: 23, instruction: 'Exhale through your mouth for 8 counts', duration: 8 },
      { time: 31, instruction: 'Repeat this cycle 3 more times', duration: 89 }
    ]
  },
  {
    id: 'wrist-release',
    category: 'stretch',
    name: 'Wrist & Hand Release',
    duration: 90,
    icon: '🤲',
    steps: [
      { time: 0, instruction: 'Extend your right arm straight out in front of you', duration: 10 },
      { time: 10, instruction: 'Point fingers up, gently pull back with left hand, hold 20 seconds', duration: 20 },
      { time: 30, instruction: 'Point fingers down, gently pull, hold 20 seconds', duration: 20 },
      { time: 50, instruction: 'Repeat with left arm', duration: 40 }
    ]
  },
  {
    id: 'desk-backbend',
    category: 'stretch',
    name: 'Desk Backbend',
    duration: 60,
    icon: '✨',
    steps: [
      { time: 0, instruction: 'Sit at the edge of your chair, feet flat', duration: 10 },
      { time: 10, instruction: 'Place hands on your lower back, fingers pointing down', duration: 10 },
      { time: 20, instruction: 'Gently arch your back, look up slightly, hold for 30 seconds', duration: 30 },
      { time: 50, instruction: 'Slowly return to neutral position', duration: 10 }
    ]
  },
  {
    id: 'box-breathing',
    category: 'breathing',
    name: 'Box Breathing',
    duration: 180, // 3 minutes
    icon: '📦',
    steps: [
      { time: 0, instruction: 'Sit upright, relax your shoulders', duration: 5 },
      { time: 5, instruction: 'Inhale through your nose for 4 counts', duration: 4 },
      { time: 9, instruction: 'Hold your breath for 4 counts', duration: 4 },
      { time: 13, instruction: 'Exhale through your nose for 4 counts', duration: 4 },
      { time: 17, instruction: 'Hold at the bottom for 4 counts', duration: 4 },
      { time: 21, instruction: 'Repeat this cycle 7 more times', duration: 159 }
    ]
  },
  {
    id: 'forward-fold',
    category: 'stretch',
    name: 'Seated Forward Fold',
    duration: 75,
    icon: '🧘',
    steps: [
      { time: 0, instruction: 'Sit tall, feet flat on floor, knees bent', duration: 10 },
      { time: 10, instruction: 'Slowly fold forward, letting your head hang', duration: 15 },
      { time: 25, instruction: 'Hold this position, breathe deeply for 30 seconds', duration: 30 },
      { time: 55, instruction: 'Slowly roll back up, one vertebra at a time', duration: 20 }
    ]
  }
];

function MicroResets() {
  const [activeReset, setActiveReset] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const filteredResets = selectedCategory === 'all' 
    ? RESETS 
    : RESETS.filter(r => r.category === selectedCategory);

  const handleComplete = useCallback(() => {
    setActiveReset(null);
    setTimeRemaining(0);
    setCurrentStep(0);
    setIsPaused(false);
    // Could show a completion message here
  }, []);

  useEffect(() => {
    let interval = null;
    if (activeReset && !isPaused && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(prev => {
          const newTime = prev - 1;
          
          // Update current step based on elapsed time
          if (activeReset) {
            const elapsed = activeReset.duration - newTime;
            const stepIndex = activeReset.steps.findIndex((step) => {
              const stepEndTime = step.time + step.duration;
              return elapsed >= step.time && elapsed < stepEndTime;
            });
            
            if (stepIndex !== -1) {
              setCurrentStep(prevStep => stepIndex !== prevStep ? stepIndex : prevStep);
            }
          }
          
          if (newTime <= 0) {
            return 0;
          }
          return newTime;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [activeReset, isPaused, timeRemaining]);

  // Handle completion when timer reaches 0
  useEffect(() => {
    if (activeReset && timeRemaining === 0 && !isPaused) {
      handleComplete();
    }
  }, [timeRemaining, activeReset, isPaused, handleComplete]);

  const startReset = (reset) => {
    setActiveReset(reset);
    setTimeRemaining(reset.duration);
    setCurrentStep(0);
    setIsPaused(false);
  };

  const togglePause = () => {
    setIsPaused(!isPaused);
  };

  const stopReset = () => {
    setActiveReset(null);
    setTimeRemaining(0);
    setCurrentStep(0);
    setIsPaused(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getCurrentInstruction = () => {
    if (!activeReset || activeReset.steps.length === 0) return '';
    const step = activeReset.steps[currentStep] || activeReset.steps[0];
    return step.instruction;
  };

  if (activeReset) {
    const progress = ((activeReset.duration - timeRemaining) / activeReset.duration) * 100;
    
    return (
      <div className="micro-resets-container">
        <Card className="micro-resets-active-card">
          <div className="active-header">
            <button className="back-button" onClick={stopReset} aria-label="Stop reset">
              ← Back
            </button>
            <h2 className="active-title title-2">{activeReset.name}</h2>
          </div>
          
          <div className="timer-display">
            <div className="timer-circle">
              <svg className="timer-svg" viewBox="0 0 100 100">
                <circle
                  className="timer-circle-bg"
                  cx="50"
                  cy="50"
                  r="45"
                />
                <circle
                  className="timer-circle-progress"
                  cx="50"
                  cy="50"
                  r="45"
                  strokeDasharray={`${2 * Math.PI * 45}`}
                  strokeDashoffset={`${2 * Math.PI * 45 * (1 - progress / 100)}`}
                />
              </svg>
              <div className="timer-text">
                <span className="timer-time">{formatTime(timeRemaining)}</span>
                <span className="timer-label caption">remaining</span>
              </div>
            </div>
          </div>

          <div className="instruction-card">
            <p className="instruction-text body-text">{getCurrentInstruction()}</p>
          </div>

          <div className="active-controls">
            <Button 
              variant="secondary" 
              onClick={togglePause}
            >
              {isPaused ? '▶ Resume' : '⏸ Pause'}
            </Button>
            <Button 
              variant="outline" 
              onClick={stopReset}
            >
              Stop
            </Button>
          </div>

          <div className="progress-indicator">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="micro-resets-container">
      <div className="micro-resets-header">
        <h1 className="title-1">Micro resets</h1>
        <p className="description body-text">
          Guided 1–3 minute stretches and breathing breaks that reduce tension and clear your head between sessions.
        </p>
      </div>

      <div className="category-filters">
        <button
          className={`category-filter ${selectedCategory === 'all' ? 'active' : ''}`}
          onClick={() => setSelectedCategory('all')}
        >
          All
        </button>
        <button
          className={`category-filter ${selectedCategory === 'stretch' ? 'active' : ''}`}
          onClick={() => setSelectedCategory('stretch')}
        >
          Stretches
        </button>
        <button
          className={`category-filter ${selectedCategory === 'breathing' ? 'active' : ''}`}
          onClick={() => setSelectedCategory('breathing')}
        >
          Breathing
        </button>
      </div>

      <div className="resets-grid">
        {filteredResets.map(reset => (
          <Card 
            key={reset.id} 
            className="reset-card"
            hoverable
            onClick={() => startReset(reset)}
          >
            <div className="reset-icon">{reset.icon}</div>
            <h3 className="reset-name title-3">{reset.name}</h3>
            <p className="reset-duration caption">
              {Math.floor(reset.duration / 60)} minute{Math.floor(reset.duration / 60) !== 1 ? 's' : ''}
            </p>
            <Button 
              variant="primary" 
              className="reset-start-button"
              onClick={(e) => {
                e.stopPropagation();
                startReset(reset);
              }}
            >
              Start
            </Button>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default MicroResets;

